import React from 'react';
import { useParams } from 'react-router-dom';

import { useQueryClient } from '@tanstack/react-query';
import { BUSINESS_TOOL } from 'constants/paths';
import dayjs from 'dayjs';
import { businessPlanQueryKeys } from 'services/businessPlan/businessPlan-qk';
import { useUpdateBusinessPlan } from 'services/businessPlan/hooks';
import { useCreateBusinessPlanFrom } from 'services/businessPlan/hooks/useCreateBusinessPlanFrom';
import { useBusinessToolStore } from 'store/useBusinessToolStore';
import {
  getBusinessPlan,
  getBusinessPlanType,
  getSetBusinessPlan,
  getSetBusinessPlanType,
} from 'store/useBusinessToolStore/selectors';
import { useUserLimits } from 'store/useUserLimits';
import { getUserLimits } from 'store/useUserLimits/selectors';

import { useAuthenticated } from 'hooks/useAuthenticated';
import usePromiseStatus from 'hooks/usePromiseStatus';

import { IBusinessPlanType, ILimitEntity } from 'interfaces/businessPlan';

import { SelectTemplate } from 'components/_basic/SelectTemplate';
import { ChangeDocumentTypeModal } from 'components/ChangeDocumentTypeModal';
import { useModalContext } from 'components/ModalManager';

import { getDocTitle } from 'helpers/getDocTitle';
import { getDocumentType } from 'helpers/getDocumentType';
import { insertId } from 'helpers/path';
import { shouldBusinessPlanBeingCopied } from 'helpers/shouldBusinessPlanBeingCopied';
import ToastService from 'helpers/ToastService';

export const BusinessToolType: React.FC = () => {
  const { id: businessPlanId = '' } = useParams();
  const queryClient = useQueryClient();
  const { isAuthenticated, isAuthConfiguring } = useAuthenticated();
  const { showModal, hideModal } = useModalContext();
  const { track, isPending } = usePromiseStatus();

  const { availableDocumentCount } = useUserLimits(getUserLimits);

  const businessPlan = useBusinessToolStore(getBusinessPlan);
  const businessPlanType = useBusinessToolStore(getBusinessPlanType);

  const setBusinessPlan = useBusinessToolStore(getSetBusinessPlan);
  const setPlanType = useBusinessToolStore(getSetBusinessPlanType);

  const createBusinessPlanFrom = useCreateBusinessPlanFrom();
  const updateBusinessPlan = useUpdateBusinessPlan();

  const changeBusinessPlanType = async (docTemplate: IBusinessPlanType) => {
    try {
      const params = { id: businessPlanId, body: { type: docTemplate } };

      await track(updateBusinessPlan.mutateAsync(params));
      setPlanType(docTemplate);
    } catch (error) {
      return ToastService.showError("Couldn't change business plan type");
    }
  };

  const createBusinessPlanCopy = async (docTemplate: IBusinessPlanType) => {
    try {
      if (availableDocumentCount[getDocumentType(docTemplate) as keyof ILimitEntity] < 0) {
        return ToastService.showError('The document limit has been reached');
      }

      const newBusinessPlan = await createBusinessPlanFrom.mutateAsync({
        businessPlanUuid: businessPlanId,
        businessPlanType: docTemplate,
      });

      window.open(insertId(BUSINESS_TOOL, newBusinessPlan.uuid), '_blank');
      queryClient.invalidateQueries({ queryKey: businessPlanQueryKeys.businessPlansLimits() });
    } catch (error) {
      return ToastService.showError("Couldn't create business plan copy");
    }
  };

  const showChangeDocumentTypeModal = (docTemplate: IBusinessPlanType) => {
    showModal(ChangeDocumentTypeModal, {
      docType: getDocTitle(docTemplate),
      close: hideModal,
      onCreateDoc: () => createBusinessPlanCopy(docTemplate),
    });
  };

  const unauthChangeDocumentTypeHandler = (docTemplate: IBusinessPlanType) => {
    setBusinessPlan({ ...businessPlan, updatedAt: dayjs().toISOString() });
    setPlanType(docTemplate);
  };

  const handleTemplateChange = (template: IBusinessPlanType) => {
    if (businessPlanType === getDocumentType(template)) {
      return;
    }

    if (!(isAuthenticated || isAuthConfiguring)) {
      unauthChangeDocumentTypeHandler(template);
      return;
    }

    if (
      shouldBusinessPlanBeingCopied({
        currentBusinessPlanType: businessPlanType,
        newBusinessPlanType: template,
      })
    ) {
      return changeBusinessPlanType(template);
    }

    showChangeDocumentTypeModal(template);
  };

  return (
    <SelectTemplate
      template={getDocumentType(businessPlanType)}
      onTemplateChange={handleTemplateChange}
      isLoading={isPending}
    />
  );
};
